"use client"

import { Shield, Lightbulb, Cog, Users, Headset, FileCheck } from "lucide-react"
import { FadeIn } from "../ui/fade-in"
import { Button } from "../ui/button"
import { useRef } from "react"

// Import des images depuis le répertoire des assets
import focusedProfessional from "../../assets/expertise-images/focused-cybersecurity-professional.png"
import dataSecurityAnalysis from "../../assets/expertise-images/data-security-analysis.png"
import cyberSecurityTeam from "../../assets/expertise-images/cyber-security-team.png"
import securityOverview from "../../assets/expertise-images/comprehensive-security-overview.png"
import diverseTeam from "../../assets/expertise-images/diverse-security-team.png"
import empatheticTechSupport from "../../assets/expertise-images/empathetic-tech-support.png"

import "../../styles/marquee.css"

export function ExpertiseSection() {
  const sectionRef = useRef<HTMLElement>(null)

  const expertiseAreas = [
    {
      icon: <Lightbulb className="w-10 h-10 text-red-600" />,
      title: "Services Conseil",
      description:
          "Notre équipe de consultants vous accompagne dans l'élaboration de votre stratégie de sécurité, l'architecture de solutions et le développement de produits sur mesure adaptés à vos besoins spécifiques.",
      image: focusedProfessional,
    },
    {
      icon: <Shield className="w-10 h-10 text-red-600" />,
      title: "Services Experts",
      description:
          "Nos services experts constituent une véritable assurance au-delà de la mise en œuvre initiale. Nous fournissons des heures d'assistance dédiées pour vous aider dans vos tâches quotidiennes de sécurité.",
      image: dataSecurityAnalysis,
    },
    {
      icon: <Cog className="w-10 h-10 text-red-600" />,
      title: "Services Managés",
      description:
          "Nous gérons et supervisons vos solutions de sécurité d'accès privilégiés, permettant à votre équipe de se concentrer sur ses activités principales pendant que nos experts veillent sur votre infrastructure.",
      image: cyberSecurityTeam,
    },
    {
      icon: <FileCheck className="w-10 h-10 text-red-600" />,
      title: "Évaluation de l'État Actuel",
      description:
          "Notre processus d'évaluation analyse vos besoins en tant qu'entreprise et conçoit un plan d'implémentation adapté, en réalisant une évaluation de maturité via notre plateforme d'analyse de sécurité.",
      image: securityOverview,
    },
    {
      icon: <Users className="w-10 h-10 text-red-600" />,
      title: "Services Professionnels",
      description:
          "Notre équipe de professionnels hautement qualifiés prend en charge la gestion et l'exécution de vos projets de sécurité, garantissant une mise en œuvre efficace et conforme aux meilleures pratiques.",
      image: diverseTeam,
    },
    {
      icon: <Headset className="w-10 h-10 text-red-600" />,
      title: "Services de Support",
      description:
          "Nos forfaits de support offrent un accès à nos ingénieurs spécialisés pour résoudre rapidement tout problème technique, avec des heures dédiées et une assistance personnalisée pour votre équipe.",
      image: empatheticTechSupport,
    },
  ]

  return (
      <section id="expertise" className="py-24 bg-black relative overflow-hidden" ref={sectionRef}>
        {/* Background gradients and vignette effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black to-black/95"></div>

        {/* Vignette effect */}
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn direction="up">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Nos <span className="text-red-600">Services</span> de Cybersécurité
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Chez S&M, nous proposons une gamme complète de services pour accompagner les intégrateurs dans la mise en
                œuvre des meilleures solutions de cybersécurité pour leurs clients.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertiseAreas.map((area, index) => (
                <FadeIn key={index} direction="up" delay={0.1 * index} threshold={0.2}>
                  <div className="rounded-xl overflow-hidden h-full flex flex-col shadow-lg transition-colors duration-300 group">
                    {/* Image container - Increased height */}
                    <div className="relative h-64 md:h-72 lg:h-80 w-full overflow-hidden">
                      <img
                          src={area.image || "/placeholder.svg"}
                          alt={area.title}
                          className="w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-105"
                      />

                      {/* Overlay with icon and title */}
                      <div className="bg-gradient-to-t from-zinc-900 to-zinc-900/0 absolute inset-0 flex flex-col justify-end p-6">
                        <div className="flex items-start gap-4 mb-3">
                          <div className="bg-red-600/20 p-3 rounded-full">{area.icon}</div>
                          <h3 className="text-2xl font-bold text-white mt-2">{area.title}</h3>
                        </div>
                      </div>
                    </div>

                    {/* Description with gradient, opacity and blur */}
                    <div className="bg-zinc-900 backdrop-blur-md p-6 flex-grow flex flex-col">
                      <p className="text-gray-300 mb-6 flex-grow">{area.description}</p>
                      <Button variant="primary" className="mt-auto self-start bg-red-600 hover:bg-red-700 text-white">
                        En savoir plus
                      </Button>
                    </div>
                  </div>
                </FadeIn>
            ))}
          </div>


        </div>
      </section>
  )
}
